import { useId, memo } from 'react';

/**
 * Detailed iPhone illustration with 3 viewpoints (front, back, camera cluster)
 * and 4 condition states (perfect, micro, visible, broken).
 *
 * Quality focus:
 *  - Multi-stop metallic gradients for titanium feel
 *  - Layered glass surfaces with reflection highlights
 *  - Hand-crafted scratch paths (Bézier curves, varying stroke widths + opacities)
 *  - Realistic impact crack patterns radiating from a point
 *  - Per-instance unique ids so gradients don't clash when multiple illustrations render
 */

const STATE_DOT = {
  perfect: '#10B981',
  micro: '#F59E0B',
  visible: '#F97316',
  broken: '#DC2626',
};

/* ---------- Scratch pattern generators ---------- */

// Curved hairline scratch between two points (quadratic Bézier with small bow).
const curve = (x1, y1, x2, y2, bow = 2) => {
  const cx = (x1 + x2) / 2 + bow;
  const cy = (y1 + y2) / 2 - bow;
  return `M ${x1} ${y1} Q ${cx} ${cy}, ${x2} ${y2}`;
};

// Micro-scratches — 4-5 barely visible hairlines
const microScratches = (bounds) => {
  const { x, y, w, h } = bounds;
  return [
    { d: curve(x + w * 0.15, y + h * 0.2, x + w * 0.55, y + h * 0.22, 3), sw: 0.35, op: 0.18 },
    { d: curve(x + w * 0.35, y + h * 0.42, x + w * 0.82, y + h * 0.4, -2), sw: 0.3, op: 0.14 },
    { d: curve(x + w * 0.2, y + h * 0.62, x + w * 0.7, y + h * 0.65, 2), sw: 0.35, op: 0.2 },
    { d: curve(x + w * 0.5, y + h * 0.78, x + w * 0.88, y + h * 0.82, -1.5), sw: 0.3, op: 0.15 },
    { d: curve(x + w * 0.1, y + h * 0.88, x + w * 0.45, y + h * 0.9, 1.5), sw: 0.28, op: 0.13 },
  ];
};

// Visible scratches — longer, thicker, more varied
const visibleScratches = (bounds) => {
  const { x, y, w, h } = bounds;
  return [
    { d: curve(x + w * 0.08, y + h * 0.12, x + w * 0.65, y + h * 0.18, 4), sw: 0.8, op: 0.42 },
    { d: curve(x + w * 0.45, y + h * 0.25, x + w * 0.92, y + h * 0.32, -3), sw: 0.6, op: 0.35 },
    { d: curve(x + w * 0.15, y + h * 0.45, x + w * 0.55, y + h * 0.5, 3), sw: 0.7, op: 0.38 },
    { d: curve(x + w * 0.3, y + h * 0.58, x + w * 0.85, y + h * 0.6, -2), sw: 0.5, op: 0.3 },
    { d: curve(x + w * 0.1, y + h * 0.72, x + w * 0.7, y + h * 0.78, 4), sw: 0.9, op: 0.45 },
    { d: curve(x + w * 0.55, y + h * 0.85, x + w * 0.88, y + h * 0.88, -1.5), sw: 0.5, op: 0.28 },
    { d: curve(x + w * 0.2, y + h * 0.92, x + w * 0.5, y + h * 0.95, 1), sw: 0.4, op: 0.22 },
    // cross-direction
    { d: curve(x + w * 0.7, y + h * 0.15, x + w * 0.75, y + h * 0.65, 6), sw: 0.55, op: 0.25 },
  ];
};

// Broken — impact crack radiating out + branch fractures + small chips
const brokenPattern = (bounds, impact) => {
  const { x, y, w, h } = bounds;
  const ix = impact?.x ?? x + w * 0.58;
  const iy = impact?.y ?? y + h * 0.45;

  const mainCracks = [
    // 7 main radial cracks, uneven angles
    { d: `M ${ix} ${iy} L ${x + w * 0.15} ${y + h * 0.12}`, sw: 1.3, op: 0.7 },
    { d: `M ${ix} ${iy} L ${x + w * 0.88} ${y + h * 0.08}`, sw: 1.1, op: 0.62 },
    { d: `M ${ix} ${iy} L ${x + w * 0.95} ${y + h * 0.5}`, sw: 1, op: 0.58 },
    { d: `M ${ix} ${iy} L ${x + w * 0.75} ${y + h * 0.95}`, sw: 1.2, op: 0.68 },
    { d: `M ${ix} ${iy} L ${x + w * 0.22} ${y + h * 0.95}`, sw: 1.1, op: 0.6 },
    { d: `M ${ix} ${iy} L ${x + w * 0.05} ${y + h * 0.7}`, sw: 0.95, op: 0.55 },
    { d: `M ${ix} ${iy} L ${x + w * 0.4} ${y + h * 0.02}`, sw: 0.85, op: 0.5 },
  ];

  // Branch cracks from main lines
  const branches = [
    { d: `M ${x + w * 0.55} ${y + h * 0.2} L ${x + w * 0.38} ${y + h * 0.1}`, sw: 0.6, op: 0.38 },
    { d: `M ${x + w * 0.82} ${y + h * 0.25} L ${x + w * 0.9} ${y + h * 0.15}`, sw: 0.55, op: 0.35 },
    { d: `M ${x + w * 0.88} ${y + h * 0.55} L ${x + w * 0.75} ${y + h * 0.65}`, sw: 0.6, op: 0.4 },
    { d: `M ${x + w * 0.55} ${y + h * 0.75} L ${x + w * 0.65} ${y + h * 0.85}`, sw: 0.5, op: 0.35 },
    { d: `M ${x + w * 0.35} ${y + h * 0.72} L ${x + w * 0.3} ${y + h * 0.85}`, sw: 0.55, op: 0.38 },
    { d: `M ${x + w * 0.2} ${y + h * 0.55} L ${x + w * 0.1} ${y + h * 0.5}`, sw: 0.5, op: 0.32 },
  ];

  return { mainCracks, branches, impact: { x: ix, y: iy } };
};

/* ---------- Gradients & filters (per-instance ids) ---------- */

function Defs({ uid, face }) {
  return (
    <defs>
      {/* Titanium/aluminum frame */}
      <linearGradient id={`frame-${uid}`} x1="0" y1="0" x2="1" y2="0.2">
        <stop offset="0" stopColor="#3a3d48" />
        <stop offset="0.3" stopColor="#6b6f7c" />
        <stop offset="0.55" stopColor="#8b8f9c" />
        <stop offset="0.85" stopColor="#565966" />
        <stop offset="1" stopColor="#32343d" />
      </linearGradient>

      {/* Inner bezel shadow */}
      <linearGradient id={`bezel-${uid}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#14151a" />
        <stop offset="1" stopColor="#070810" />
      </linearGradient>

      {/* Screen glass (front) */}
      <linearGradient id={`screen-${uid}`} x1="0" y1="0" x2="0.6" y2="1">
        <stop offset="0" stopColor="#1a1d25" />
        <stop offset="0.4" stopColor="#0a0c13" />
        <stop offset="1" stopColor="#161920" />
      </linearGradient>

      {/* Screen reflection — diagonal light sweep */}
      <linearGradient id={`shine-${uid}`} x1="0.15" y1="0" x2="0.75" y2="1">
        <stop offset="0" stopColor="#ffffff" stopOpacity="0.14" />
        <stop offset="0.15" stopColor="#ffffff" stopOpacity="0.04" />
        <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
        <stop offset="0.85" stopColor="#ffffff" stopOpacity="0.02" />
        <stop offset="1" stopColor="#ffffff" stopOpacity="0.08" />
      </linearGradient>

      {/* Back glass (pro graphite) */}
      <linearGradient id={`back-${uid}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#2e3140" />
        <stop offset="0.5" stopColor="#1a1c26" />
        <stop offset="1" stopColor="#2e3140" />
      </linearGradient>

      {/* Back reflection */}
      <linearGradient id={`backShine-${uid}`} x1="0" y1="0" x2="0.7" y2="1">
        <stop offset="0" stopColor="#ffffff" stopOpacity="0.07" />
        <stop offset="0.4" stopColor="#ffffff" stopOpacity="0" />
        <stop offset="1" stopColor="#ffffff" stopOpacity="0.03" />
      </linearGradient>

      {/* Camera bump */}
      <linearGradient id={`bump-${uid}`} x1="0" y1="0" x2="0.7" y2="1">
        <stop offset="0" stopColor="#3a3d4a" />
        <stop offset="0.5" stopColor="#262934" />
        <stop offset="1" stopColor="#1a1c26" />
      </linearGradient>

      {/* Lens body (outer ring) */}
      <radialGradient id={`lensOuter-${uid}`} cx="0.35" cy="0.35" r="0.7">
        <stop offset="0" stopColor="#6b6f80" />
        <stop offset="0.5" stopColor="#2a2d38" />
        <stop offset="1" stopColor="#0b0c12" />
      </radialGradient>

      {/* Lens glass (inner) */}
      <radialGradient id={`lensGlass-${uid}`} cx="0.4" cy="0.35" r="0.8">
        <stop offset="0" stopColor="#1f2838" />
        <stop offset="0.5" stopColor="#070a12" />
        <stop offset="1" stopColor="#000000" />
      </radialGradient>

      {/* Tiny blue aperture highlight */}
      <radialGradient id={`aperture-${uid}`} cx="0.5" cy="0.5" r="0.5">
        <stop offset="0" stopColor="#4a7ee8" stopOpacity="0.9" />
        <stop offset="0.6" stopColor="#1a2d58" stopOpacity="0.4" />
        <stop offset="1" stopColor="#000000" stopOpacity="0" />
      </radialGradient>

      {/* Shadow filter */}
      <filter id={`shadow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="2" />
      </filter>

      {/* Glass inner shadow */}
      <filter id={`innerShadow-${uid}`} x="0" y="0" width="100%" height="100%">
        <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
        <feOffset dx="0" dy="1" />
        <feComposite in2="SourceAlpha" operator="arithmetic" k2="-1" k3="1" result="inner" />
        <feColorMatrix in="inner" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.5 0" />
      </filter>

      {/* Subtle noise for realism (only used on broken state chips) */}
      {face && (
        <pattern id={`noise-${uid}`} x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
          <rect width="4" height="4" fill="white" opacity="0.02" />
          <circle cx="1" cy="1" r="0.3" fill="white" opacity="0.08" />
          <circle cx="3" cy="2" r="0.25" fill="white" opacity="0.06" />
        </pattern>
      )}
    </defs>
  );
}

/* ---------- Front face ---------- */

function FrontFace({ uid, state }) {
  const screenBounds = { x: 10, y: 10, w: 180, h: 380 };
  const clipId = `screenClip-${uid}`;

  return (
    <>
      {/* Frame outer */}
      <rect x="2" y="2" width="196" height="396" rx="38" ry="38" fill={`url(#frame-${uid})`} />

      {/* Inner frame shadow */}
      <rect x="6" y="6" width="188" height="388" rx="34" ry="34" fill={`url(#bezel-${uid})`} />

      {/* Screen glass */}
      <rect x="10" y="10" width="180" height="380" rx="30" ry="30" fill={`url(#screen-${uid})`} />

      {/* Glass reflection */}
      <rect x="10" y="10" width="180" height="380" rx="30" ry="30" fill={`url(#shine-${uid})`} />

      {/* Dynamic Island */}
      <rect x="74" y="22" width="52" height="15" rx="7.5" ry="7.5" fill="#000" />
      <circle cx="118" cy="29.5" r="1.6" fill="#1a2838" />

      {/* Side buttons */}
      <rect x="-0.5" y="78" width="4" height="26" rx="1.5" fill="#2e3140" />
      <rect x="-0.5" y="116" width="4" height="42" rx="1.5" fill="#2e3140" />
      <rect x="-0.5" y="168" width="4" height="42" rx="1.5" fill="#2e3140" />
      <rect x="196.5" y="128" width="4" height="56" rx="1.5" fill="#2e3140" />

      {/* Subtle screen inner border (darker edge) */}
      <rect
        x="10"
        y="10"
        width="180"
        height="380"
        rx="30"
        ry="30"
        fill="none"
        stroke="#000"
        strokeOpacity="0.4"
        strokeWidth="1"
      />

      {/* Scratches (clipped inside screen area) */}
      <clipPath id={clipId}>
        <rect x="10" y="10" width="180" height="380" rx="30" ry="30" />
      </clipPath>
      <ScratchLayer state={state} bounds={screenBounds} clipId={clipId} impact={{ x: 110, y: 180 }} />
    </>
  );
}

/* ---------- Back face ---------- */

function BackFace({ uid, state }) {
  const backBounds = { x: 10, y: 10, w: 180, h: 380 };
  const clipId = `backClip-${uid}`;

  return (
    <>
      <rect x="2" y="2" width="196" height="396" rx="38" ry="38" fill={`url(#frame-${uid})`} />

      {/* Back glass */}
      <rect x="8" y="8" width="184" height="384" rx="34" ry="34" fill={`url(#back-${uid})`} />
      <rect x="8" y="8" width="184" height="384" rx="34" ry="34" fill={`url(#backShine-${uid})`} />

      {/* Camera bump (iPhone Pro style — square with 3 lenses) */}
      <rect x="22" y="22" width="76" height="76" rx="22" ry="22" fill={`url(#bump-${uid})`} />

      {/* Bump inner subtle border */}
      <rect x="22" y="22" width="76" height="76" rx="22" ry="22" fill="none" stroke="#000" strokeOpacity="0.35" strokeWidth="0.8" />

      {/* Lenses (3 + LiDAR + flashlight) */}
      <Lens cx={42} cy={42} r={13} uid={uid} />
      <Lens cx={76} cy={42} r={13} uid={uid} />
      <Lens cx={42} cy={76} r={13} uid={uid} />
      {/* LiDAR */}
      <circle cx="78" cy="76" r="5" fill="#1a1d28" />
      <circle cx="78" cy="76" r="3" fill="#2a3850" />
      <circle cx="78" cy="76" r="1.2" fill="#5a7cb4" opacity="0.8" />
      {/* Flashlight */}
      <circle cx="78" cy="62" r="3.5" fill="#2a2d38" />
      <circle cx="78" cy="62" r="2.2" fill="#8b8f9c" />
      <circle cx="78" cy="62" r="1" fill="#f0eadf" />

      {/* Discreet logo placeholder (circle) */}
      <circle cx="100" cy="180" r="10" fill="white" opacity="0.04" />

      {/* Side buttons */}
      <rect x="-0.5" y="78" width="4" height="26" rx="1.5" fill="#2e3140" />
      <rect x="-0.5" y="116" width="4" height="42" rx="1.5" fill="#2e3140" />
      <rect x="-0.5" y="168" width="4" height="42" rx="1.5" fill="#2e3140" />
      <rect x="196.5" y="128" width="4" height="56" rx="1.5" fill="#2e3140" />

      {/* Scratches (avoiding camera bump area) */}
      <clipPath id={clipId}>
        <rect x="8" y="8" width="184" height="384" rx="34" ry="34" />
      </clipPath>
      <ScratchLayer state={state} bounds={backBounds} clipId={clipId} impact={{ x: 130, y: 260 }} />
    </>
  );
}

/* ---------- Camera close-up ---------- */

function CameraFace({ uid, state }) {
  return (
    <>
      {/* Back glass background */}
      <rect x="0" y="0" width="200" height="200" fill={`url(#back-${uid})`} rx="20" ry="20" />
      <rect x="0" y="0" width="200" height="200" fill={`url(#backShine-${uid})`} rx="20" ry="20" />

      {/* Camera bump — large, centered */}
      <rect x="30" y="30" width="140" height="140" rx="38" ry="38" fill={`url(#bump-${uid})`} />
      <rect x="30" y="30" width="140" height="140" rx="38" ry="38" fill="none" stroke="#000" strokeOpacity="0.4" strokeWidth="1.5" />

      {/* 3 lenses */}
      <Lens cx={72} cy={72} r={26} uid={uid} detailed />
      <Lens cx={128} cy={72} r={26} uid={uid} detailed />
      <Lens cx={72} cy={128} r={26} uid={uid} detailed />
      {/* LiDAR */}
      <circle cx="134" cy="134" r="12" fill="#1a1d28" />
      <circle cx="134" cy="134" r="8" fill="#2a3850" />
      <circle cx="134" cy="134" r="3" fill="#5a7cb4" opacity="0.9" />
      {/* Flash */}
      <circle cx="134" cy="110" r="6" fill="#2a2d38" />
      <circle cx="134" cy="110" r="4" fill="#8b8f9c" />
      <circle cx="134" cy="110" r="1.6" fill="#f5eedf" />

      {/* Camera-specific scratches / damage on lenses */}
      <CameraScratches uid={uid} state={state} />
    </>
  );
}

function Lens({ cx, cy, r, uid, detailed }) {
  return (
    <g>
      {/* Outer metal ring */}
      <circle cx={cx} cy={cy} r={r} fill={`url(#lensOuter-${uid})`} />
      {/* Glass well */}
      <circle cx={cx} cy={cy} r={r * 0.72} fill={`url(#lensGlass-${uid})`} />
      {/* Aperture glow */}
      <circle cx={cx} cy={cy} r={r * 0.38} fill={`url(#aperture-${uid})`} />
      {/* Tiny center reflection */}
      <ellipse
        cx={cx - r * 0.22}
        cy={cy - r * 0.28}
        rx={r * 0.18}
        ry={r * 0.1}
        fill="white"
        opacity={detailed ? 0.22 : 0.14}
      />
      {detailed && (
        <ellipse
          cx={cx + r * 0.25}
          cy={cy + r * 0.25}
          rx={r * 0.08}
          ry={r * 0.05}
          fill="white"
          opacity="0.15"
        />
      )}
    </g>
  );
}

/* ---------- Scratch layer (for front/back) ---------- */

function ScratchLayer({ state, bounds, clipId, impact }) {
  if (state === 'perfect') return null;

  if (state === 'micro') {
    return (
      <g clipPath={`url(#${clipId})`} stroke="#e0e8f0" fill="none" strokeLinecap="round">
        {microScratches(bounds).map((s, i) => (
          <path key={i} d={s.d} strokeWidth={s.sw} opacity={s.op} />
        ))}
      </g>
    );
  }

  if (state === 'visible') {
    return (
      <g clipPath={`url(#${clipId})`} stroke="#e6ecf2" fill="none" strokeLinecap="round">
        {visibleScratches(bounds).map((s, i) => (
          <path key={i} d={s.d} strokeWidth={s.sw} opacity={s.op} />
        ))}
      </g>
    );
  }

  // broken
  const { mainCracks, branches, impact: imp } = brokenPattern(bounds, impact);
  return (
    <g clipPath={`url(#${clipId})`}>
      {/* Faint shattered haze */}
      <rect
        x={bounds.x}
        y={bounds.y}
        width={bounds.w}
        height={bounds.h}
        fill="white"
        opacity="0.03"
      />
      <g stroke="#e2e7ef" fill="none" strokeLinecap="round">
        {mainCracks.map((c, i) => (
          <path key={`m${i}`} d={c.d} strokeWidth={c.sw} opacity={c.op} />
        ))}
        {branches.map((c, i) => (
          <path key={`b${i}`} d={c.d} strokeWidth={c.sw} opacity={c.op} />
        ))}
      </g>
      {/* Impact point */}
      <circle cx={imp.x} cy={imp.y} r="2.5" fill="white" opacity="0.85" />
      <circle cx={imp.x} cy={imp.y} r="5" fill="white" opacity="0.15" />
      {/* A few small chips */}
      <polygon
        points={`${imp.x - 8},${imp.y - 3} ${imp.x - 4},${imp.y - 10} ${imp.x + 2},${imp.y - 6} ${imp.x - 2},${imp.y - 1}`}
        fill="white"
        opacity="0.18"
      />
      <polygon
        points={`${imp.x + 12},${imp.y + 8} ${imp.x + 18},${imp.y + 6} ${imp.x + 20},${imp.y + 12} ${imp.x + 14},${imp.y + 14}`}
        fill="white"
        opacity="0.12"
      />
    </g>
  );
}

/* ---------- Camera-specific scratches ---------- */

function CameraScratches({ uid, state }) {
  if (state === 'perfect') return null;

  if (state === 'micro') {
    return (
      <g stroke="#e4ebf2" fill="none" strokeLinecap="round" opacity="0.5">
        {/* Small concentric arc scratches on lens glass */}
        <path d="M 58 65 A 16 16 0 0 1 88 70" strokeWidth="0.5" opacity="0.5" />
        <path d="M 114 75 A 18 14 0 0 1 142 78" strokeWidth="0.45" opacity="0.4" />
        <path d="M 60 128 A 20 18 0 0 1 92 132" strokeWidth="0.5" opacity="0.45" />
      </g>
    );
  }

  if (state === 'visible') {
    return (
      <g stroke="#eef3f8" fill="none" strokeLinecap="round">
        {/* Diagonal scratches across multiple lenses */}
        <path d="M 48 58 Q 90 85, 150 110" strokeWidth="0.8" opacity="0.42" />
        <path d="M 55 95 Q 100 100, 152 90" strokeWidth="0.6" opacity="0.35" />
        <path d="M 58 140 Q 95 145, 148 138" strokeWidth="0.7" opacity="0.4" />
        {/* Concentric arcs on lenses */}
        <path d="M 58 65 A 18 18 0 0 1 90 72" strokeWidth="0.6" opacity="0.5" />
        <path d="M 110 72 A 20 16 0 0 1 145 80" strokeWidth="0.55" opacity="0.45" />
      </g>
    );
  }

  // broken — one cracked lens (top-left)
  return (
    <g>
      {/* Crack pattern on one lens */}
      <g stroke="#eef3f8" fill="none" strokeLinecap="round">
        <path d="M 72 72 L 52 52" strokeWidth="1.2" opacity="0.85" />
        <path d="M 72 72 L 92 58" strokeWidth="1" opacity="0.75" />
        <path d="M 72 72 L 82 94" strokeWidth="1.1" opacity="0.8" />
        <path d="M 72 72 L 56 88" strokeWidth="0.9" opacity="0.7" />
        <path d="M 72 72 L 92 82" strokeWidth="0.8" opacity="0.65" />
        {/* Branches */}
        <path d="M 62 62 L 58 58" strokeWidth="0.5" opacity="0.5" />
        <path d="M 82 58 L 88 52" strokeWidth="0.4" opacity="0.45" />
      </g>
      {/* Impact point */}
      <circle cx="72" cy="72" r="2" fill="white" opacity="0.9" />
      <circle cx="72" cy="72" r="4" fill="white" opacity="0.2" />
      {/* Chips */}
      <polygon points="64,64 60,58 68,60 66,66" fill="white" opacity="0.22" />
      <polygon points="80,78 86,80 82,86" fill="white" opacity="0.18" />

      {/* Second lens — scratched but not broken */}
      <g stroke="#eef3f8" fill="none" strokeLinecap="round">
        <path d="M 110 70 A 18 16 0 0 1 144 78" strokeWidth="0.7" opacity="0.4" />
        <path d="M 108 68 L 148 82" strokeWidth="0.5" opacity="0.3" />
      </g>
    </g>
  );
}

/* ---------- Main component ---------- */

const PhoneIllustration = memo(function PhoneIllustration({
  face = 'front',
  state = 'perfect',
  size = 120,
  className = '',
}) {
  const uid = useId().replace(/:/g, '');
  const isCamera = face === 'camera';
  const viewBox = isCamera ? '0 0 200 200' : '0 0 200 400';
  const aspectH = isCamera ? size : size * 2;

  return (
    <svg
      viewBox={viewBox}
      width={size}
      height={aspectH}
      className={className}
      style={{ display: 'block', overflow: 'visible' }}
      role="img"
      aria-label={`iPhone ${face} en état ${state}`}
    >
      <Defs uid={uid} face={face} />

      {/* Soft drop shadow under phone */}
      {!isCamera && (
        <ellipse cx="100" cy="402" rx="80" ry="5" fill="black" opacity="0.22" filter={`url(#shadow-${uid})`} />
      )}

      {face === 'front' && <FrontFace uid={uid} state={state} />}
      {face === 'back' && <BackFace uid={uid} state={state} />}
      {face === 'camera' && <CameraFace uid={uid} state={state} />}
    </svg>
  );
});

export default PhoneIllustration;
export { STATE_DOT };
